import * as CL from '@design-system/component-library';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { hideMsg, t, viewAllMsg } from '../../common/i18n/index.js';
import { onDateValueChange, parseIsoDate } from '../../common/utils/dateUtils.js';
import { onEnterOrSpaceKeyPress } from '../../common/utils/handlerUtils.js';
import { ButtonGroupForSubmitAndBack } from '../ButtonGroupForSubmitAndBack/ButtonGroupForSubmitAndBack.js';
import { resetPagingParams } from './tableUtils.js';
import type { ReactNode } from 'react';

import './SearchFilters.scss';

export interface SearchFilterCocoGroup {
  label: () => string;
  value: string;
  items: SearchFilterCoco[];
  displayAllItems?: boolean;
  headerToolTip?: ReactNode;
}

export interface SearchFilterCoco {
  label: () => string;
  value: string;
  checked?: boolean;
  itemsCount?: number;
}

export interface SearchFilterCocoDateRange {
  label: () => string;
  fromLabel: () => string;
  toLabel: () => string;
  fromParam: string;
  toParam: string;
}

export interface SearchFilterCocosProps {
  filterGroups: Array<SearchFilterCocoGroup>;
  dateRange?: SearchFilterCocoDateRange;
  displayActiveFiltersAsTiles?: boolean;
  visibleFiltersCountPerGroup?: number;
  onModalClose: () => void;
  onFilterChange?: (queryParameters: URLSearchParams) => void;
}

interface ActiveFilterTilesProps {
  filterGroups: Array<SearchFilterCocoGroup>;
  onClearFilters: () => void;
  onFilterItemUpdated: (filterItem: SearchFilterCoco) => void;
  anyFilterSelected: boolean;
  dateRange?: SearchFilterCocoDateRange;
  dateRangeValues?: { from: string; to: string };
  onClearDateRange: () => void;
}

interface SearchFilterCocosCheckboxGroupProps {
  filterGroup: SearchFilterCocoGroup;
  visibleFiltersCount?: number;
  onFilterItemUpdated: (filterItem: SearchFilterCoco) => void;
}

interface SearchFilterCocosGridProps {
  filterGroups: Array<SearchFilterCocoGroup>;
  onFilterItemUpdated: (filterItem: SearchFilterCoco) => void;
  onClearFilters: () => void;
  onViewAllFiltersUpdated: (filterGroup: SearchFilterCocoGroup) => void;
  anyFilterSelected: boolean;
  visibleFiltersCount?: number;
  isLoading?: boolean;
  dateRange?: SearchFilterCocoDateRange;
  dateRangeValues?: { from: string; to: string };
  onDateRangeChange: (param: string, value: string) => void;
}

interface SearchFilterCocosModalProps {
  filterGroups: Array<SearchFilterCocoGroup>;
  onFilterItemUpdated: (filterItem: SearchFilterCoco) => void;
  onModalClose: () => void;
  isLoading?: boolean;
  onFilterAndCloseModal: () => void;
  onClearFiltersAndCloseModal: () => void;
  anyFilterSelected: boolean;
  dateRange?: SearchFilterCocoDateRange;
  dateRangeValues?: { from: string; to: string };
  onDateRangeChange: (param: string, value: string) => void;
}

const DEFAULT_VISIBLE_FILTERS_PER_GROUP = 10;

interface SearchFilterCocosDateRangeProps {
  dateRange: SearchFilterCocoDateRange;
  values: { from: string; to: string };
  idPrefix: string;
  onChange: (param: string, value: string) => void;
}

const DatePicker = ({
  idPrefix,
  fromTo,
  value,
  label,
  param,
  onChange,
}: {
  idPrefix: string;
  fromTo: string;
  value: string;
  label: string;
  param: string;
  onChange: (param: string, value: string) => void;
}) => {
  const key = `${idPrefix}-${fromTo}:${value}`;
  const inputStartDateId = `${idPrefix}-${fromTo}`;
  return (
    <CL.DatePicker
      key={key}
      inputStartDateId={inputStartDateId}
      i18n_datepicker_start_label={label}
      selectedDay={parseIsoDate(value)}
      onValueChange={val => onDateValueChange(val, param, onChange)}
    />
  );
};

const SearchFilterCocosDateRange = ({ dateRange, values, idPrefix, onChange }: SearchFilterCocosDateRangeProps) => (
  <div className="of-date-filter">
    <div className="of-checkbox-group__label-text">{dateRange.label()}</div>
    <div className="of-date-filter__inputs">
      <DatePicker
        idPrefix={idPrefix}
        fromTo="from"
        value={values.from}
        label={dateRange.fromLabel()}
        param={dateRange.fromParam}
        onChange={onChange}
      />
      <DatePicker
        idPrefix={idPrefix}
        fromTo="to"
        value={values.to}
        label={dateRange.toLabel()}
        param={dateRange.toParam}
        onChange={onChange}
      />
    </div>
  </div>
);

const ActiveFilterTiles = ({
  filterGroups,
  onClearFilters,
  onFilterItemUpdated,
  anyFilterSelected,
  dateRange,
  dateRangeValues,
  onClearDateRange,
}: ActiveFilterTilesProps) => {
  const activeFilters = filterGroups
    .map(filterGroup => {
      return filterGroup.items
        .filter(item => item.checked)
        .map(item => {
          return {
            label: () => `${filterGroup.label()}: ${item.label()}`,
            value: item.value,
          };
        });
    })
    .flat();

  return (
    <div className={`of-active-filter-tiles-container${anyFilterSelected ? '__active' : ''}`}>
      <div className="of-active-filter-tiles">
        {activeFilters.map(activeFilter => (
          <div
            className="of-active-filter-tiles__tile"
            key={activeFilter.value}
            onClick={() => onFilterItemUpdated(activeFilter)}
            onKeyDown={(event: React.KeyboardEvent) => {
              if (event.key !== 'Tab') {
                onEnterOrSpaceKeyPress(event, () => {
                  onFilterItemUpdated(activeFilter);
                });
              }
            }}
            role="button"
            tabIndex={0}
          >
            <span className={dsClass.MARGIN_RIGHT_1}>{activeFilter.label()}</span>
            <CL.Icon icon="close" size="s" />
          </div>
        ))}
        {dateRange && dateRangeValues && (dateRangeValues.from || dateRangeValues.to) && (
          <button className="of-active-filter-tiles__tile" type="button" onClick={onClearDateRange}>
            <span className={dsClass.MARGIN_RIGHT_1}>
              {dateRange.label()}: {dateRange.fromLabel()} {dateRangeValues.from || '-'}, {dateRange.toLabel()}{' '}
              {dateRangeValues.to || '-'}
            </span>
            <CL.Icon icon="close" size="s" />
          </button>
        )}
      </div>
      {activeFilters.length > 0 && (
        <div className="of-clear-filters-button">
          <CL.Button className={dsClass.MARGIN_LEFT_3} color="link" size="s" onClick={onClearFilters}>
            {t.HH00('Clear')}
          </CL.Button>
        </div>
      )}
    </div>
  );
};

const SearchFilterCocosCheckboxGroup = ({
  filterGroup,
  visibleFiltersCount,
  onFilterItemUpdated,
}: SearchFilterCocosCheckboxGroupProps) => (
  <div className="of-checkbox-group">
    <CL.Checkbox.Group className={dsClass.MARGIN_BOTTOM_1}>
      <div className="of-checkbox-group__header">
        <span className="of-checkbox-group__label-text">{filterGroup.label()}</span>
        {filterGroup.headerToolTip}
      </div>
      {filterGroup.items.map(
        (filterItem, index) =>
          (visibleFiltersCount === undefined || filterGroup.displayAllItems || index < visibleFiltersCount) && (
            <CL.Checkbox
              // TODO: Workaround to force CL.Checkbox to rerender after checked prop update. See https://atlas.elisa.fi/jira/browse/DS-1592.
              key={`${filterItem.value}-${filterItem.checked === true ? 'checked' : 'unchecked'}`}
              value={filterItem.value}
              checked={filterItem.checked}
              onChange={() => onFilterItemUpdated(filterItem)}
            >
              <div>
                <span>{filterItem.label()}</span>
                {filterItem.itemsCount !== undefined && <span>&nbsp;({filterItem.itemsCount})</span>}
              </div>
            </CL.Checkbox>
          )
      )}
    </CL.Checkbox.Group>
  </div>
);

const SearchFilterCocosGrid = ({
  filterGroups,
  onFilterItemUpdated,
  onClearFilters,
  onViewAllFiltersUpdated,
  anyFilterSelected,
  visibleFiltersCount,
  dateRange,
  dateRangeValues,
  onDateRangeChange,
}: SearchFilterCocosGridProps) => {
  const filterColumnCount = filterGroups.length + (dateRange ? 1 : 0);
  const fitDateRangeOnSameRow = dateRange && filterColumnCount <= 4;
  const colWidthL = fitDateRangeOnSameRow ? Math.min(6, Math.floor(12 / filterColumnCount)) : 6;
  const colWidthXL = fitDateRangeOnSameRow ? Math.min(4, Math.floor(12 / filterColumnCount)) : 4;

  return (
    <CL.Grid className="of-search-filters-grid">
      <>
        <CL.GridRow
          className={`${dsClass.PADDING_TOP_5} ${dsClass.PADDING_RIGHT_5} ${dsClass.PADDING_LEFT_5} ${dsClass.PADDING_BOTTOM_1}`}
        >
          {filterGroups.map(filterGroup => (
            <CL.GridCol
              key={filterGroup.value}
              className={`of-checkbox-group ${dsClass.PADDING_0} ${dsClass.MARGIN_BOTTOM_4}`}
              colWidthS={6}
              colWidthM={3}
              colWidthL={colWidthL}
              colWidthXL={colWidthXL}
            >
              <SearchFilterCocosCheckboxGroup
                filterGroup={filterGroup}
                visibleFiltersCount={visibleFiltersCount}
                onFilterItemUpdated={onFilterItemUpdated}
              />
              {visibleFiltersCount && filterGroup.items.length > visibleFiltersCount && (
                <div className="of-toggle-view-items-button">
                  <CL.Button color="link" onClick={() => onViewAllFiltersUpdated(filterGroup)}>
                    {filterGroup.displayAllItems ? t.OJPZ(hideMsg) : t.SF4C(viewAllMsg)}
                  </CL.Button>
                </div>
              )}
            </CL.GridCol>
          ))}
          {dateRange && dateRangeValues && (
            <CL.GridCol
              className={`of-checkbox-group ${dsClass.PADDING_0} ${dsClass.MARGIN_BOTTOM_4}`}
              colWidthS={6}
              colWidthM={3}
              colWidthL={colWidthL}
              colWidthXL={colWidthXL}
            >
              <SearchFilterCocosDateRange
                dateRange={dateRange}
                values={dateRangeValues}
                idPrefix="search-filter-date-grid"
                onChange={onDateRangeChange}
              />
            </CL.GridCol>
          )}
        </CL.GridRow>
        {anyFilterSelected && (
          <div className={`${dsClass.MARGIN_RIGHT_3} ${dsClass.MARGIN_BOTTOM_3} ${dsClass.TEXT_ALIGN_RIGHT}`}>
            <CL.Button className={dsClass.FONT_SIZE_SMALL} color="link" size="m" onClick={onClearFilters}>
              {t.HH00('Clear')}
            </CL.Button>
          </div>
        )}
      </>
    </CL.Grid>
  );
};

const SearchFilterCocosModal = ({
  filterGroups,
  onModalClose,
  isLoading,
  onFilterAndCloseModal,
  onFilterItemUpdated,
  onClearFiltersAndCloseModal,
  anyFilterSelected,
  dateRange,
  dateRangeValues,
  onDateRangeChange,
}: SearchFilterCocosModalProps) => (
  <div className="of-search-filters-modal">
    <CL.Modal autoOpen={true} disableBodyScroll={false} onModalClose={onModalClose}>
      <h2>{t.XUFV('Filter')}</h2>
      {isLoading && <CL.LoadingSpinner className="loading-spinner" size="l" />}
      {!isLoading && (
        <>
          {filterGroups.map((filterGroup: SearchFilterCocoGroup) => (
            <div className={dsClass.MARGIN_BOTTOM_4} key={filterGroup.value}>
              <SearchFilterCocosCheckboxGroup
                filterGroup={{ ...filterGroup, displayAllItems: true }}
                onFilterItemUpdated={onFilterItemUpdated}
              />
            </div>
          ))}
          {dateRange && dateRangeValues && (
            <div className={dsClass.MARGIN_BOTTOM_4}>
              <SearchFilterCocosDateRange
                dateRange={dateRange}
                values={dateRangeValues}
                idPrefix="search-filter-date-modal"
                onChange={onDateRangeChange}
              />
            </div>
          )}
          <div className="of-search-filters-modal__sticky-buttons">
            <ButtonGroupForSubmitAndBack
              className={`${dsClass.PADDING_TOP_4} ${dsClass.TEXT_ALIGN_RIGHT}`}
              submitButtonText={t.X49N('Show')}
              onSubmit={onFilterAndCloseModal}
              cancelDisabled={!anyFilterSelected}
              cancelButtonText={t.HH00('Clear')}
              onCancel={onClearFiltersAndCloseModal}
            />
          </div>
        </>
      )}
    </CL.Modal>
  </div>
);

const processFilterGroups = (filterGroups: SearchFilterCocoGroup[]) => {
  return filterGroups.map(group => ({
    ...group,
    displayAllItems: group.displayAllItems ?? false,
  }));
};

export const SearchFiltersCoco = ({
  filterGroups,
  dateRange,
  displayActiveFiltersAsTiles,
  visibleFiltersCountPerGroup = DEFAULT_VISIBLE_FILTERS_PER_GROUP,
  onModalClose,
  onFilterChange,
}: SearchFilterCocosProps) => {
  const navigate = useNavigate();
  const { hash, pathname, search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const dateRangeValues = dateRange
    ? {
        from: queryParams.get(dateRange.fromParam) ?? '',
        to: queryParams.get(dateRange.toParam) ?? '',
      }
    : undefined;
  const [filterGroupsState, setFilterGroupsState] = useState(processFilterGroups(filterGroups));

  useEffect(() => {
    setFilterGroupsState(processFilterGroups(filterGroups));
  }, [filterGroups]);

  const createFilterQuery = (filterGroupsUpdated: Array<SearchFilterCocoGroup>) => {
    const filterQuery = new URLSearchParams();
    filterGroupsUpdated.forEach(filterGroup => {
      const selectedItems = filterGroup.items.filter(item => item.checked).map(item => item.value);
      if (selectedItems.length > 0) {
        filterQuery.set(filterGroup.value, selectedItems.join('|'));
      }
    });
    return filterQuery;
  };

  const filterResults = (filterQuery: URLSearchParams, clearDateRangeParams = false) => {
    filterGroups?.forEach(filter => {
      const filterValue = filterQuery.get(filter.value);
      if (filterValue) {
        queryParams.set(filter.value, filterValue);
      } else {
        queryParams.delete(filter.value);
      }
    });
    if (clearDateRangeParams && dateRange) {
      queryParams.delete(dateRange.fromParam);
      queryParams.delete(dateRange.toParam);
    }
    // return to first page when filters updated
    resetPagingParams(queryParams);
    navigate(`${pathname}?${queryParams}${hash}`, { replace: true });
    onFilterChange?.(filterQuery);
  };

  const updateDateRange = (param: string, value: string) => {
    const updatedParams = new URLSearchParams(search);
    if (value) {
      updatedParams.set(param, value);
    } else {
      updatedParams.delete(param);
    }
    resetPagingParams(updatedParams);
    navigate(`${pathname}?${updatedParams}${hash}`, { replace: true });
  };

  const clearDateRange = () => {
    if (!dateRange) {
      return;
    }
    const updatedParams = new URLSearchParams(search);
    updatedParams.delete(dateRange.fromParam);
    updatedParams.delete(dateRange.toParam);
    resetPagingParams(updatedParams);
    navigate(`${pathname}?${updatedParams}${hash}`, { replace: true });
  };

  const getUpdatedFilterGroups = (filterItem: SearchFilterCoco) =>
    filterGroupsState.map(filterGroup => ({
      ...filterGroup,
      items: filterGroup.items.map(item =>
        item.value === filterItem.value ? { ...item, checked: !item.checked } : item
      ),
    }));

  const updateFilters = (filterItem: SearchFilterCoco) => {
    const updatedFilterGroups = getUpdatedFilterGroups(filterItem);
    setFilterGroupsState(updatedFilterGroups);
  };

  const updateFiltersImmediate = (filterItem: SearchFilterCoco) => {
    const updatedFilterGroups = getUpdatedFilterGroups(filterItem);
    setFilterGroupsState(updatedFilterGroups);
    filterResults(createFilterQuery(updatedFilterGroups));
  };

  const clearFilters = () => {
    const updatedFilterGroups = filterGroupsState.map(filterGroup => {
      return {
        ...filterGroup,
        items: filterGroup.items.map(item => ({
          ...item,
          checked: false,
        })),
      };
    });
    setFilterGroupsState(updatedFilterGroups);
    filterResults(createFilterQuery(updatedFilterGroups), true);
  };

  const toggleViewAllFilters = (filterGroup: SearchFilterCocoGroup) =>
    setFilterGroupsState(
      filterGroupsState.map(group =>
        group.value === filterGroup.value ? { ...group, displayAllItems: !filterGroup.displayAllItems } : group
      )
    );

  const anyFilterSelected = () =>
    filterGroupsState.some(filterGroup => filterGroup.items.some(item => item.checked)) ||
    Boolean(dateRangeValues?.from || dateRangeValues?.to);

  const closeModal = () => {
    setFilterGroupsState(filterGroups.map(group => ({ ...group, displayAllItems: group.displayAllItems ?? false })));
    onModalClose();
  };

  const clearFiltersAndCloseModal = () => {
    clearFilters();
    onModalClose();
  };

  const filterAndCloseModal = () => {
    filterResults(createFilterQuery(filterGroupsState));
    onModalClose();
  };

  if (displayActiveFiltersAsTiles) {
    return (
      <ActiveFilterTiles
        filterGroups={filterGroupsState}
        onClearFilters={clearFilters}
        onFilterItemUpdated={(filterItem: SearchFilterCoco) => updateFiltersImmediate(filterItem)}
        anyFilterSelected={anyFilterSelected()}
        dateRange={dateRange}
        dateRangeValues={dateRangeValues}
        onClearDateRange={clearDateRange}
      />
    );
  } else {
    return (
      <div className="of-search-filters">
        <SearchFilterCocosGrid
          filterGroups={filterGroupsState}
          onFilterItemUpdated={(filterItem: SearchFilterCoco) => updateFiltersImmediate(filterItem)}
          onClearFilters={clearFilters}
          onViewAllFiltersUpdated={toggleViewAllFilters}
          anyFilterSelected={anyFilterSelected()}
          visibleFiltersCount={visibleFiltersCountPerGroup}
          dateRange={dateRange}
          dateRangeValues={dateRangeValues}
          onDateRangeChange={updateDateRange}
        />
        <SearchFilterCocosModal
          filterGroups={filterGroupsState}
          onFilterItemUpdated={(filterItem: SearchFilterCoco) => updateFilters(filterItem)}
          onModalClose={closeModal}
          onFilterAndCloseModal={filterAndCloseModal}
          onClearFiltersAndCloseModal={clearFiltersAndCloseModal}
          anyFilterSelected={anyFilterSelected()}
          dateRange={dateRange}
          dateRangeValues={dateRangeValues}
          onDateRangeChange={updateDateRange}
        />
      </div>
    );
  }
};
